import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="status-refresh"
export default class extends Controller {
  static targets = ["timestamp", "statusBadge", "timeline"]
  static values = {
    url: String,
    interval: { type: Number, default: 30000 }, // 30 seconds default
    enabled: { type: Boolean, default: true }
  }

  connect() {
    this.startRefreshing()
    this.updateTimestamp()
  }

  disconnect() {
    this.stopRefreshing()
  }

  startRefreshing() {
    if (this.enabledValue && !this.refreshTimer) {
      this.refreshTimer = setInterval(() => {
        this.refresh()
      }, this.intervalValue)
    }
  }

  stopRefreshing() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  // Manual refresh triggered by button click
  async manualRefresh(event) {
    event?.preventDefault()
    const button = event?.currentTarget

    // Disable button and show loading state
    if (button) {
      button.disabled = true
      button.classList.add("opacity-50", "cursor-not-allowed")

      // Add spinning animation to icon if present
      const icon = button.querySelector("svg")
      if (icon) {
        icon.classList.add("animate-spin")
      }
    }

    try {
      await this.refresh()
    } finally {
      // Re-enable button and remove loading state
      if (button) {
        button.disabled = false
        button.classList.remove("opacity-50", "cursor-not-allowed")

        const icon = button.querySelector("svg")
        if (icon) {
          icon.classList.remove("animate-spin")
        }
      }
    }
  }

  async refresh() {
    if (!this.hasUrlValue) {
      console.warn("Status refresh URL not configured")
      return
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin"
      })

      if (response.ok) {
        const html = await response.text()

        // Let Turbo handle the stream response
        Turbo.renderStreamMessage(html)

        // Update timestamp
        this.updateTimestamp()

        // Dispatch custom event for other controllers to react
        this.dispatch("refreshed", { detail: { timestamp: new Date() } })
      } else {
        console.error("Status refresh failed:", response.status)
        this.handleError(response)
      }
    } catch (error) {
      console.error("Status refresh error:", error)
      this.handleError(error)
    }
  }

  updateTimestamp() {
    if (this.hasTimestampTarget) {
      const now = new Date()
      this.timestampTarget.textContent = "Updated just now"
      this.timestampTarget.dataset.lastUpdate = now.toISOString()

      // Start updating relative time
      this.startTimestampUpdater()
    }
  }

  startTimestampUpdater() {
    if (this.timestampUpdater) {
      clearInterval(this.timestampUpdater)
    }

    this.timestampUpdater = setInterval(() => {
      if (this.hasTimestampTarget && this.timestampTarget.dataset.lastUpdate) {
        const lastUpdate = new Date(this.timestampTarget.dataset.lastUpdate)
        const secondsAgo = Math.floor((new Date() - lastUpdate) / 1000)

        if (secondsAgo < 60) {
          this.timestampTarget.textContent = `Updated ${secondsAgo} seconds ago`
        } else if (secondsAgo < 3600) {
          const minutesAgo = Math.floor(secondsAgo / 60)
          this.timestampTarget.textContent = `Updated ${minutesAgo} minute${minutesAgo > 1 ? 's' : ''} ago`
        } else {
          const hoursAgo = Math.floor(secondsAgo / 3600)
          this.timestampTarget.textContent = `Updated ${hoursAgo} hour${hoursAgo > 1 ? 's' : ''} ago`
        }
      }
    }, 1000) // Update every second
  }

  handleError(error) {
    // Dispatch error event for error handling controllers to react
    this.dispatch("error", { detail: { error } })

    // Show error message to user
    this.showErrorMessage("Unable to refresh status. Please try again later.")
  }

  showErrorMessage(message) {
    // Create a temporary flash message
    const flashContainer = document.getElementById("flash-messages")
    if (flashContainer) {
      const alert = document.createElement("div")
      alert.className = "bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded relative mb-4"
      alert.setAttribute("role", "alert")
      alert.innerHTML = `
        <span class="block sm:inline">${message}</span>
      `

      flashContainer.appendChild(alert)

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        alert.remove()
      }, 5000)
    }
  }

  // Toggle auto-refresh on/off
  toggleAutoRefresh(event) {
    this.enabledValue = event.target.checked

    if (this.enabledValue) {
      this.startRefreshing()
    } else {
      this.stopRefreshing()
    }
  }

  // Clean up on disconnect
  disconnectCallback() {
    this.stopRefreshing()

    if (this.timestampUpdater) {
      clearInterval(this.timestampUpdater)
      this.timestampUpdater = null
    }
  }
}
